import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, RefreshCw } from 'lucide-react';

const OfflineIndicator: React.FC = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [showReconnected, setShowReconnected] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);

    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            setShowReconnected(true);
            setTimeout(() => setShowReconnected(false), 3000);
            // Check for pending requests
            checkPendingRequests();
        };

        const handleOffline = () => {
            setIsOnline(false);
            setShowReconnected(false);
        };

        // Listen for SW sync messages
        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === 'SYNC_COMPLETE') {
                setPendingCount(0);
            }
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        navigator.serviceWorker?.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            navigator.serviceWorker?.removeEventListener('message', handleMessage);
        };
    }, []);

    const checkPendingRequests = async () => {
        try {
            const request = indexedDB.open('hmpos-offline', 1);
            request.onsuccess = () => {
                const db = request.result;
                if (db.objectStoreNames.contains('pendingRequests')) {
                    const tx = db.transaction('pendingRequests', 'readonly');
                    const store = tx.objectStore('pendingRequests');
                    const countReq = store.count();
                    countReq.onsuccess = () => setPendingCount(countReq.result);
                }
            };
        } catch { /* ignore */ }
    };

    // Don't show anything when online and no recent reconnection
    if (isOnline && !showReconnected) return null;

    return (
        <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
            isOnline ? 'animate-fade-in' : ''
        }`}>
            {!isOnline ? (
                <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-600 text-white rounded-full shadow-lg text-sm font-medium">
                    <WifiOff className="w-4 h-4" />
                    <span>You are offline — cached data shown</span>
                    {pendingCount > 0 && (
                        <span className="flex items-center gap-1 ml-1 px-2 py-0.5 bg-amber-700 rounded-full text-xs">
                            <RefreshCw className="w-3 h-3" /> {pendingCount} pending
                        </span>
                    )}
                </div>
            ) : showReconnected ? (
                <div className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-full shadow-lg text-sm font-medium">
                    <Wifi className="w-4 h-4" />
                    <span>Back online — syncing data</span>
                </div>
            ) : null}
        </div>
    );
};

export default OfflineIndicator;
